import React, { Component } from 'react';
import axios from 'axios';
import Config from '../../config';
import { Tabs, Tab, Grid as GridBase, Card, Table, TableBody, TableHead, TableRow, TableCell, Checkbox, LinearProgress, Toolbar, CardContent, Button } from '@mui/material';
import ExtendedSelectBase from '../../components/select';
import Helper, { DocumentHelper } from '../../helpers';
import { GetRuleDescription } from '../inventory/shared/validation';
import { withRouter } from "react-router";
import FileSaverBase from 'filesaver.js-npm';
import Papa from 'papaparse';
import type { DocumentDetailsProps, DocumentDetailsState } from './types';

// Relax MUI Grid and untyped legacy/3rd-party modules to `any` (behavior
// unchanged).
const Grid: any = GridBase;
const ExtendedSelect: any = ExtendedSelectBase;
const FileSaver: any = FileSaverBase;

const docTypes = ['Order', 'POAck', 'AdvanceShipmentNotice', 'Invoice', 'Return', 'Inventory'].map(r => ({label: new DocumentHelper().getApiDocumentType(r), value: r}));


const abbreviate = (docType: string) => {
    const abbreviations: Record<string, string> = {
        'order': 'Order',
        'shipment': 'Ship.',
        'advanceshipmentnotice': 'Ship.',
        'invoice': 'Inv.',
        'acknowledgement': 'Ack.',
        'poack': 'Ack.',
        'return': 'Ret.',
        'inventory': 'Inv.'
    };
    
    const normalizedType = docType.toLowerCase().replace(/\s+/g, '');
    return abbreviations[normalizedType] || docType;
};

class DocumentStandards extends Component<DocumentDetailsProps, DocumentDetailsState> {
    state: DocumentDetailsState = {selectedTab: "Order"};
    async componentDidMount(){
        if(this.props.setTitle) {
            this.props.setTitle('Doc. Standards');
        }
        let partner = new Helper().queryString()['partner'];
        let type = new Helper().queryString()['type'];
        let rules = (await axios.get(Config.api + "/odata/Company/ValidationDtos")).data.value || [];
        let partners = rules.filter((r, i) => rules.findIndex(x => x.CoId === r.CoId && x.CompanyName === r.CompanyName) === i).map(r => ({CompanyName: r.CompanyName, CoId: r.CoId}));
        rules.forEach(r => {
            r.Rules.forEach(rule => {
                this.processRule(rule);
                (rule.Conditions || []).forEach(this.processRule);
                let oldConditions = rule.Conditions || [];
                rule.Conditions = oldConditions.filter(x => !(x.Type === "required" && x.Field === "Identifier.LogicbrokerKey"));
                rule.Validation = GetRuleDescription({...rule});
                rule.Required = rule.Type === "required" && rule.Conditions.length === 0;
                if(rule.Conditions.length !== oldConditions.length){
                    rule.Validation = (rule.Validation + "\nThis field may be automatically populated by Logicbroker.").trim();
                    rule.Required = true;
                }
            });
            r.Rules = r.Rules.sort((a, b) => {
                if(a.Required !== b.Required){
                    return a.Required && !b.Required ? -1 : 1;
                }
                return a.Field > b.Field ? 1 : a.Field < b.Field ? -1 : 0;
            });
        });
        let firstCoId = partners.filter(r => r.CoId.toString() === partner).length > 0 ? parseInt(partner) : (partners[0] || {}).CoId;
        let selectedTab = (docTypes.filter(r => r.value.toLowerCase() === (type || '').toLowerCase())[0] || {}).value || this.state.selectedTab;
        this.setState({loaded: true, partners, allRules: rules, partner: firstCoId, rules: rules.filter(r => r.CoId === firstCoId), selectedTab }, () => {
            if(this.props.setTitle && firstCoId) {
                const partner = partners.find(p => p.CoId === firstCoId);
                const docType = new DocumentHelper().getApiDocumentType(selectedTab);
                if(partner) {
                    this.props.setTitle(`${abbreviate(docType)}: ${partner.CompanyName}`, true);
                }
            }
        });
    }
    processRule = (rule) => {
        rule.DescriptiveField = rule.Field;
        rule.Parameters = rule.Parameters ? rule.Parameters.reduce((map: Record<string, any>, obj) => {map[obj.Key] = obj.Value; return map;}, {}) : null;
    }
    onPartnerSwitch = (e) => {
        if(!e){
            return;
        }
        let rules = this.state.allRules.filter(r => r.CoId === e);
        new Helper().setUrlObject('partner', e, this.props.history, this.props.location);
        this.setState({partner: e, rules});
        if(this.props.setTitle) {
            const partner = this.state.partners.find(p => p.CoId === e);
            const docType = new DocumentHelper().getApiDocumentType(this.state.selectedTab);
            if(partner) {
                this.props.setTitle(`${abbreviate(docType)}: ${partner.CompanyName}`, true);
            }
        }
    }
    switchTab = (e, newValue) => {
        new Helper().setUrlObject('type', newValue, this.props.history, this.props.location);
        this.setState({selectedTab: newValue}, () => {
            if(this.props.setTitle && this.state.partner) {
                const partner = this.state.partners.find(p => p.CoId === this.state.partner);
                const docType = new DocumentHelper().getApiDocumentType(newValue);
                if(partner) {
                    this.props.setTitle(`${abbreviate(docType)}: ${partner.CompanyName}`, true);
                }
            }
        });
    }
    downloadValidation = () => {
        let rules = ((this.state.rules || []).filter(r => r.DocumentType === this.state.selectedTab)[0] || {}).Rules || [];
        var rows = [["Field", "External Field", "Sample Data", "Required", "Description", "Validations"]];
        rules.forEach(rule => {
            rows.push([rule.Field,
                rule.ExternalField,
                rule.Sample,
                (rule.Required || "").toString().toUpperCase(),
                rule.Description,
                rule.Validation
            ]);
        });
        var blob = new Blob([Papa.unparse(rows)], { type: "application/octet-stream" });
        FileSaver.saveAs(blob, `standards_${this.state.partner}_${new DocumentHelper().getApiDocumentType(this.state.selectedTab).toLowerCase()}.csv`);
    }
    render(){
        let rules = ((this.state.rules || []).filter(r => r.DocumentType === this.state.selectedTab)[0] || {}).Rules || [];
        return <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{'Company'}</Toolbar>
                    <CardContent>
                        <div style={{display: 'flex', flex: '1', flexDirection: 'column'}}>
                        {this.state.partners &&
                            <ExtendedSelect variant='outlined' placeholder='Select company...' onChange={this.onPartnerSwitch} value={this.state.partner} options={this.state.partners.map((i) => {return {label: i.CompanyName, value: i.CoId};})}/>
                        }
                        </div>
                        {!this.state.loaded && <LinearProgress style={{marginTop: '0.5rem', height: '5px'}}/>}
                    </CardContent>
                </Card>
            </Grid>
            <>
            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>
                            <span style={{flex: 1, textAlign: 'left'}}>{'Validation Rules'}</span>
                            <Button variant='text' onClick={this.downloadValidation} sx={{color: 'white', '&:hover': {backgroundColor: 'transparent'}}}>Export</Button>
                            </Toolbar>

                        <Tabs value={this.state.selectedTab} onChange={this.switchTab}>
                        {docTypes.map(r => <Tab key={r.value} label={r.label} value={r.value}></Tab>)}
                        </Tabs>
                        <div>
                        <Table style={{marginBottom: '10px'}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{width: '12em'}}>Field</TableCell>
                                    <TableCell style={{width: '12em'}}>External Field</TableCell>
                                    <TableCell style={{width: '12em'}}>Sample Data</TableCell>
                                    <TableCell style={{width: '5em'}}>Required</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Validations</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {rules.length === 0 && <TableRow style={{ height: '5rem' }}>
                              <TableCell colSpan={6} style={{textAlign: 'center'}}>There are no validation rules configured for this document type.</TableCell>
                            </TableRow>}
                            {rules.map((r, i) => <TableRow key={i}>
                            <TableCell>{r.Field}</TableCell>
                            <TableCell>{r.ExternalField}</TableCell>
                            <TableCell>{r.Sample}</TableCell>
                            <TableCell><Checkbox checked={r.Required} style={{pointerEvents: 'none'}}/></TableCell>
                            <TableCell style={{whiteSpace: "pre-wrap"}}>{r.Description}</TableCell>
                            <TableCell style={{whiteSpace: "pre-wrap"}}>{r.Validation}</TableCell>
                            </TableRow>)}
                            </TableBody>
                            </Table>
                        </div>
                  </Card>
            </Grid>
            </>
        </Grid>;
    }
}

// withRouter's TS overload narrows wrapped props to RouteComponentProps; cast to
// `any` since this legacy class component also receives app-shell props
// (setTitle). Behavior unchanged.
export default withRouter(DocumentStandards as any);
